import { ReactNode, createElement, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useDesignTokens, DesignTokens, renderInlineCssVars } from '../../../theme';
import { useSliderDrag, SliderTrackClientBounds } from '../useSliderDrag';
import { SliderScale } from '../SliderScale';
import { useIsRTL } from '../../../locale';
import { GripType } from '../SliderValues';
import { postfixTestId, TestIdProps } from '../../../util/testId';
import { InvisibleText } from '../../InvisibleText';
import { GlobalSettingsKeys, useGetGlobalSetting } from '../../Settings';

import cx from 'classnames';
import cn from './SliderValueGrip.css.json';
import './SliderValueGrip.css';

export interface SliderGripKeyAction {
  type: 'step' | 'stepTwo' | 'stepLarge' | 'stepMax';
  dir: 'forward' | 'backward';
}

export interface SliderGripRef {
  focusGrip(): void;
  getGripElement(): HTMLDivElement | null;
  getGripContainerElement(): HTMLDivElement | null;
}

export interface SliderGripProps extends TestIdProps {
  clientBounds: SliderTrackClientBounds;
  inclusive: boolean;
  value: number;
  formattedValue: string;
  minValue: number;
  maxValue: number;
  scale: SliderScale;
  orientation: 'vertical' | 'horizontal';
  arrowGrips: boolean;
  type: GripType;
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel: string;
  ariaLabelledBy: string;
  ariaDescribedBy?: string;
  /**
   * HC uses this to stop handles that are on top of each other from getting stuck (the
   * last one that was moved will be on top).
   */
  zIndex: number;
  children?: ReactNode;
  /** Tells whether this slider has ticks/tick labels */
  sliderHasTicks?: boolean;

  onDragStart?: () => void;
  onChange?: (event: { position: number; didDrag?: boolean }) => void;
  onCommit?: (event: { position: number; didDrag?: boolean }) => void;
  onKeyAction?: (event: SliderGripKeyAction) => void;
  onClick?: () => void;
}

export const SliderGrip = forwardRef<SliderGripRef, SliderGripProps>(function SliderGrip(
  props,
  ref
) {
  const { startDrag, didDrag } = useSliderDrag({
    clientBounds: props.clientBounds,
    orientation: props.orientation,
    minPosition: props.scale.position(props.minValue),
    maxPosition: props.scale.position(props.maxValue),
    onChange: props.onChange,
    onCommit: props.onCommit,
  });

  const dt = useDesignTokens();

  // Check if a custom focus indicator is set. If so, we'll display the focus indicator
  // regardless of mouse/keyboard interaction
  const customFocusIndicator = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';

  const [enableFocusOutline, setEnableFocusOutline] = useState(true);

  const clsName = cx(
    props.disabled && cn.disabled,
    props.orientation === 'vertical' ? cn.vertical : cn.horizontal,
    props.arrowGrips ? cn.arrowgrip : '',
    // when you add your noTicksSliderGrip style, you can uncomment the following line
    // !props.sliderHasTicks && cn.noTicksSliderGrip,
    enableFocusOutline && cn.gripEnableFocusOutline
  );

  const isRTL = useIsRTL();

  const position = props.scale.position(props.value);

  const style = {
    ...renderInlineCssVars(getSliderGripThemeTokens(dt), 'slider-grip'),
    zIndex: props.zIndex,
    ...(props.orientation === 'vertical' // prettier
      ? { top: `${(1 - position) * 100}%` }
      : { [isRTL ? 'right' : 'left']: `${position * 100}%` }),
  };

  const refGrip = useRef<HTMLDivElement>(null);
  const refGripContainer = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      focusGrip() {
        if (refGrip.current) refGrip.current.focus();
      },
      getGripElement() {
        return refGrip.current;
      },
      getGripContainerElement() {
        return refGripContainer.current;
      },
    }),
    [refGrip]
  );

  return (
    <div
      data-testid={postfixTestId(props.testId, 'container')}
      className={cn.container[props.orientation]}
      ref={refGripContainer}
    >
      <InvisibleText
        id={props.ariaLabelledBy}
        testId={postfixTestId(props.testId, 'invisibleText')}
      >
        {props.ariaLabel}
      </InvisibleText>
      <div
        data-testid={props.testId}
        className={clsName}
        style={style}
        ref={refGrip}
        role="slider"
        tabIndex={0}
        aria-labelledby={props.ariaLabelledBy}
        aria-orientation={props.orientation}
        aria-valuetext={props.formattedValue}
        aria-valuenow={props.value}
        aria-valuemin={props.minValue}
        aria-valuemax={props.maxValue}
        aria-disabled={props.disabled}
        aria-describedby={props.ariaDescribedBy}
        // NOVACOMMONS-8942: don't set aria-readonly if the slider is disabled
        aria-readonly={props.disabled ? undefined : props.readOnly}
        onMouseDown={
          props.disabled || props.readOnly
            ? undefined
            : (e) => {
                // only process left clicks
                if (e.button !== 0) return;
                // need to prevent default action, which starts a native DOM drag-and-drop sometimes.
                e.preventDefault();
                startDrag([e.clientX, e.clientY], position);
                if (refGrip.current) {
                  // focus the grip so it can receive keyboard input and closes any grip inputs
                  refGrip.current.focus();
                }
                if (!customFocusIndicator) {
                  // don't show focus outline when the user drags a grip
                  setEnableFocusOutline(false);
                }
                props.onDragStart && props.onDragStart();
              }
        }
        onClick={
          props.disabled || props.readOnly
            ? undefined
            : () => {
                if (!didDrag && props.onClick) {
                  props.onClick();
                }
              }
        }
        onKeyDown={
          props.disabled || props.readOnly
            ? undefined
            : (e) => {
                const handleKeyAction = (action: SliderGripKeyAction) => {
                  // NOTE: each of these keys could cause scrolling to occur, so we preventDefault in those cases.
                  e.preventDefault();
                  // show focus outline when the user uses the keyboard
                  setEnableFocusOutline(true);
                  props.onKeyAction && props.onKeyAction(action);
                };
                switch (e.key) {
                  case 'ArrowRight':
                    handleKeyAction({
                      type: e.shiftKey ? 'stepTwo' : 'step',
                      dir: isRTL ? 'backward' : 'forward',
                    });
                    break;
                  case 'ArrowUp':
                    handleKeyAction({
                      type: e.shiftKey ? 'stepTwo' : 'step',
                      dir: 'forward',
                    });
                    break;
                  case 'ArrowLeft':
                    handleKeyAction({
                      type: e.shiftKey ? 'stepTwo' : 'step',
                      dir: isRTL ? 'forward' : 'backward',
                    });
                    break;
                  case 'ArrowDown':
                    handleKeyAction({
                      type: e.shiftKey ? 'stepTwo' : 'step',
                      dir: 'backward',
                    });
                    break;
                  case 'Home':
                    handleKeyAction({ type: 'stepMax', dir: 'backward' });
                    break;
                  case 'End':
                    handleKeyAction({ type: 'stepMax', dir: 'forward' });
                    break;
                  case 'PageUp':
                    handleKeyAction({ type: 'stepLarge', dir: 'forward' });
                    break;
                  case 'PageDown':
                    handleKeyAction({ type: 'stepLarge', dir: 'backward' });
                    break;
                }
              }
        }
        onBlur={
          props.disabled || props.readOnly
            ? undefined
            : () => {
                // reset focus, so if a user tabs into the slider grip it'll show an outline
                setEnableFocusOutline(true);
              }
        }
      >
        {props.inclusive ? (
          <div
            className={cx(
              props.arrowGrips ? cn.arrowIndicator : cn.indicator,
              props.disabled && cn.disabled
            )}
          />
        ) : null}
        {props.children}
      </div>
    </div>
  );
});

const getSliderGripThemeTokens = (dt: DesignTokens) => {
  if (dt.enableNovaTokens) {
    return {
      bg: dt.stateSecondaryToggleNova.normal.bg,
      bd: dt.stateSecondaryToggleNova.normal.bd,
      'disabled-bd': dt.stateSecondaryToggleNova.disabled.fg,
      'indicator-bg': dt.stateSecondaryToggleNova.selected.bg,
      'spacing-s': dt.spacing.core.s,
      'indicator-disabled-bg': dt.stateSecondaryToggleNova.disabledSelected.bg,
    };
  } else {
    return {
      bg: dt.stateSecondaryToggle.normal.bg,
      bd: dt.stateSecondaryToggle.normal.bd,
      'indicator-bg': dt.stateSecondaryToggle.selected.bg,
      'spacing-s': dt.spacing.core.s,
    };
  }
};
